import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatRupiah, formatDateTime, PAYMENT_LABEL } from "@/lib/api";
import { DELIVERY_LABEL } from "@/lib/shipping";
import { useStore } from "@/context/StoreContext";
import { WhatsAppShare } from "@/components/WhatsAppShare";

export const ReceiptHeader = ({ store }) => (
  <div className="text-center">
    <p className="text-sm font-bold" data-testid="receipt-store-name">{store.name || "Toko Saya"}</p>
    {store.address && <p className="whitespace-pre-line" data-testid="receipt-store-address">{store.address}</p>}
    {store.whatsapp && <p data-testid="receipt-store-whatsapp">WA: {store.whatsapp}</p>}
    <p className="mt-1">Terima kasih telah berbelanja</p>
  </div>
);

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between ${bold ? "font-bold" : ""}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export const ReceiptDialog = ({ tx, onClose }) => {
  const { store } = useStore();
  return (
  <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="receipt-dialog">
      <DialogTitle className="no-print font-display">Struk Transaksi</DialogTitle>
      {tx && (
        <div className="print-area mx-auto w-full max-w-[320px] bg-white p-4 font-mono text-xs text-black" data-testid="receipt-content">
          <ReceiptHeader store={store} />
          {tx.status === "dibatalkan" && (
            <p className="my-2 border-2 border-red-600 py-1 text-center text-sm font-bold text-red-600" data-testid="receipt-voided-stamp">DIBATALKAN</p>
          )}
          <div className="my-2 border-t border-dashed border-black" />
          <Row label="No" value={<span data-testid="receipt-invoice-no">{tx.invoice_no}</span>} />
          <Row label="Tanggal" value={formatDateTime(tx.created_at)} />
          <Row label="Kasir" value={tx.cashier} />
          <div className="my-2 border-t border-dashed border-black" />
          {tx.items.map((i, idx) => (
            <div key={idx} className="mb-1">
              <p>{i.name}</p>
              {i.option_label && <p className="pl-2" data-testid={`receipt-item-options-${idx}`}>+ {i.option_label}</p>}
              <Row label={`${i.qty} x ${formatRupiah(i.price)}`} value={formatRupiah(i.subtotal)} />
            </div>
          ))}
          <div className="my-2 border-t border-dashed border-black" />
          {tx.shipping_fee > 0 && <Row label="Subtotal" value={formatRupiah(tx.subtotal)} />}
          {tx.delivery_method === "antar" && <Row label="Ongkir" value={<span data-testid="receipt-shipping">{formatRupiah(tx.shipping_fee)}</span>} />}
          <Row label="TOTAL" value={<span data-testid="receipt-total">{formatRupiah(tx.total)}</span>} bold />
          <Row label="Bayar" value={PAYMENT_LABEL[tx.payment_method]} />
          {tx.payment_method === "tunai" && (
            <>
              <Row label="Tunai" value={formatRupiah(tx.cash_paid)} />
              <Row label="Kembali" value={<span data-testid="receipt-change">{formatRupiah(tx.change)}</span>} bold />
            </>
          )}
          {tx.note && <p className="mt-2">Catatan: {tx.note}</p>}
          <p className="mt-2" data-testid="receipt-delivery">Pengiriman: {DELIVERY_LABEL[tx.delivery_method] || DELIVERY_LABEL.ambil}</p>
          {tx.delivery_address && <p data-testid="receipt-delivery-address">Alamat: {tx.delivery_address}</p>}
          <div className="my-2 border-t border-dashed border-black" />
          <p className="text-center">Barang yang sudah dibeli tidak dapat ditukar</p>
        </div>
      )}
      {tx && <WhatsAppShare tx={tx} store={store} />}
      <div className="no-print flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onClose} data-testid="receipt-close-button">Tutup</Button>
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => window.print()} data-testid="receipt-print-button">
          <Printer className="mr-2 h-4 w-4" /> Cetak Struk
        </Button>
      </div>
    </DialogContent>
  </Dialog>
  );
};
